import { FC, useEffect } from "react"
import { Form, Modal, Typography } from "antd"
import { AbExperiment, AccountOption, UpdateVariantForceAccountsData } from "../store/types"
import { AccountSelect } from "./account-select"

const areSetsEqual = (a: Set<string>, b: Set<string>): boolean => {
  if (a.size !== b.size) return false
  for (const item of Array.from(a)) {
    if (!b.has(item)) return false
  }

  return true
}

interface VariantForceValues {
  forceOnAccountIds: AccountOption[];
  forceOffAccountIds: AccountOption[];
}

interface FormValues {
  variants: Record<string, VariantForceValues>;
}

interface Props {
  visible: boolean;
  experiment: AbExperiment | null;
  onClose: () => void;
  onSubmit: (updates: Record<string, UpdateVariantForceAccountsData>) => Promise<void>;
}

export const ForceAccountsModal: FC<Props> = ({ visible, experiment, onClose, onSubmit }) => {
  const [form] = Form.useForm<FormValues>()

  useEffect(() => {
    if (!visible || !experiment) return

    const variants: Record<string, VariantForceValues> = {}

    for (const v of experiment.variants) {
      variants[v.name] = {
        forceOnAccountIds: v.forceOnAccountIds.map(id => ({ value: id, label: id })),
        forceOffAccountIds: v.forceOffAccountIds.map(id => ({ value: id, label: id })),
      }
    }

    form.setFieldsValue({ variants })
  }, [visible, experiment, form])

  const handleOk = async() => {
    const currentExperiment = experiment
    if (!currentExperiment) {
      onClose()

      return
    }

    const values = await form.validateFields()

    const updates: Record<string, UpdateVariantForceAccountsData> = {}

    for (const v of currentExperiment.variants) {
      const entry = values.variants[v.name]
      const previousForceOn = new Set(v.forceOnAccountIds)
      const previousForceOff = new Set(v.forceOffAccountIds)

      const newForceOn = new Set(entry.forceOnAccountIds.map((o: AccountOption) => o.value))
      const newForceOff = new Set(entry.forceOffAccountIds.map((o: AccountOption) => o.value))

      const hasChanges
        = !areSetsEqual(previousForceOn, newForceOn)
        || !areSetsEqual(previousForceOff, newForceOff)

      if (!hasChanges) {
        continue
      }

      updates[v.name] = {
        forceOnAccountIds: Array.from(newForceOn),
        forceOffAccountIds: Array.from(newForceOff),
      }
    }

    if (Object.keys(updates).length > 0) {
      await onSubmit(updates)
    }

    onClose()
  }

  return (
    <Modal
      title={`Debug Force Accounts — ${experiment?.name ?? ""}`}
      open={visible}
      onCancel={onClose}
      onOk={handleOk}
      okText="Save"
      width={640}
      destroyOnClose
    >
      <Typography.Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
        Updates force on/off account IDs without ending or replacing the experiment.
      </Typography.Text>
      <Form form={form} layout="vertical">
        {experiment?.variants.map(v => (
          <div
            key={v.name}
            style={{ border: "1px solid #d9d9d9", borderRadius: 6, padding: "12px 16px", marginBottom: 12 }}
          >
            <Typography.Text strong style={{ display: "block", marginBottom: 8 }}>
              {v.name} ({(v.upperCutoffPercentage * 100).toFixed(0)}%)
            </Typography.Text>
            <Form.Item name={["variants", v.name, "forceOnAccountIds"]} label="Force ON">
              <AccountSelect placeholder="Search accounts" />
            </Form.Item>
            <Form.Item name={["variants", v.name, "forceOffAccountIds"]} label="Force OFF" style={{ marginBottom: 0 }}>
              <AccountSelect placeholder="Search accounts" />
            </Form.Item>
          </div>
        ))}
      </Form>
    </Modal>
  )
}
